import {computed, ComputedRef, inject, provide, Ref, ref} from "vue";
import {IFreshToken, INewToken} from "@/stores/AuthorizationStore/interfaces/IToken";

class TokenSetError extends Error {
    constructor() {
        super('Could not set Bearer Token!');
        this.name = "TokenSetError";
    }
}

interface IAxiosHeader {
    headers: { [key: string]: string }
}

export interface IApiCredentials {
    rootGuid: Ref<string>
    rootApiKey: Ref<string>
    guid: Ref<string>
    apikey: Ref<string>
    portalBaseUrl: string
    axiosHeader: ComputedRef<IAxiosHeader>
    rootAxiosHeader: ComputedRef<IAxiosHeader>
    axiosBearerHeader: ComputedRef<IAxiosHeader>
    rootAxiosBearerHeader: ComputedRef<IAxiosHeader>
    axiosBearerRefreshHeader: ComputedRef<IAxiosHeader>
    rootAxiosBearerRefreshHeader: ComputedRef<IAxiosHeader>
    setBearerToken: (token: IFreshToken | INewToken) => void
    setBearerRootToken: (token: IFreshToken | INewToken) => void
    bearerToken: Ref<INewToken | undefined>
    bearerRootToken: Ref<INewToken | undefined>
    setRootCredentials: (guid: string, apiKey: string) => void
    appReady: Ref<number>
}
const authorized = ref(false)
const guid = ref('')
const apikey = ref('')
// Root credentials - can be set from env vars or dynamically for SAML auth
const rootGuid = ref(import.meta.env.VITE_ROOT_GUID || '')
const rootApiKey = ref(import.meta.env.VITE_ROOT_API_KEY || '')
const bearerToken = ref<INewToken|undefined>(undefined)
const bearerRootToken = ref<INewToken|undefined>(undefined)
/**
 * Incremented by the application picker after a complete app switch
 * (new tokens acquired + permissions loaded). CrudStores watch this
 * to reload with valid credentials — avoids the race condition of
 * watching rootGuid directly.
 */
const appReady = ref(0)
/**
 * Absolute base URL for portal API requests.
 * Includes origin + portal path (e.g. https://mlservice.kpmg.at/portal/v1).
 * When used as axios URL prefix, bypasses axios.defaults.baseURL.
 */
const portalBaseUrl: string = (import.meta.env.VITE_BASE_URL || '') + (import.meta.env.VITE_PORTAL_URL || '/portal')

export const useApiCredentials = (): IApiCredentials => {
    const apimHeader: string = import.meta.env.VITE_API_HEADER

    const axiosHeader = computed<IAxiosHeader>(() => {
        return {headers: {'X-API-KEY': apikey.value, 'Ocp-Apim-Subscription-Key': apimHeader}}
    })

    const rootAxiosHeader = computed<IAxiosHeader>(() => {
        return {headers: {'X-API-KEY': rootApiKey.value, 'Ocp-Apim-Subscription-Key': apimHeader}}
    })

    const _set_token = (ref_token: Ref<IFreshToken | undefined>, token: IFreshToken | INewToken) => {
        if ('refresh_token' in token) {
            // Full token (initial acquisition or app switch) — replace
            // entirely so the refresh_token is always kept in sync.
            ref_token.value = token;
        } else if (ref_token.value != undefined) {
            // Access-only update (e.g. from /authorize/refresh) — keep
            // existing refresh_token unchanged.
            ref_token.value.access_token = token.access_token;
        } else {
            throw new TokenSetError();
        }
    }

    const setBearerToken = (token: IFreshToken | INewToken) => {
        _set_token(bearerToken, token)
    }

    const setBearerRootToken = (token: IFreshToken | INewToken) => {
        _set_token(bearerRootToken, token)
    }

    const axiosBearerHeader = computed<IAxiosHeader>(() => {
        if (bearerToken.value == undefined) {
            throw new TokenSetError()
        }
        return {headers: {'Authorization': `Bearer ${bearerToken.value.access_token}`, 'Ocp-Apim-Subscription-Key': apimHeader}}
    })
    const rootAxiosBearerHeader= computed<IAxiosHeader>(() => {
        if (bearerRootToken.value == undefined) {
            throw new TokenSetError()
        }
        return {headers: {'Authorization': `Bearer ${bearerRootToken.value.access_token}`, 'Ocp-Apim-Subscription-Key': apimHeader}}
    })
    const axiosBearerRefreshHeader= computed<IAxiosHeader>(() => {
        if (bearerToken.value == undefined) {
            throw new TokenSetError()
        }
        return {headers: {'Authorization': `Bearer ${bearerToken.value.refresh_token}`}}
    })

    const rootAxiosBearerRefreshHeader= computed<IAxiosHeader>(() => {
        if (bearerRootToken.value == undefined) {
            throw new TokenSetError()
        }
        return {headers: {'Authorization': `Bearer ${bearerRootToken.value.refresh_token}`}}
    })

    // Set root credentials dynamically (used for SAML auth where user credentials become root)
    const setRootCredentials = (newGuid: string, newApiKey: string) => {
        rootGuid.value = newGuid
        rootApiKey.value = newApiKey
    }

    return {
        rootGuid,
        rootApiKey,
        guid,
        apikey,
        portalBaseUrl,
        axiosHeader,
        rootAxiosHeader,
        axiosBearerHeader,
        rootAxiosBearerHeader,
        axiosBearerRefreshHeader,
        rootAxiosBearerRefreshHeader,
        setBearerToken,
        setBearerRootToken,
        bearerToken,
        bearerRootToken,
        setRootCredentials,
        appReady
    }
}
